<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ClockIcon, FileTextIcon, LinkIcon } from '@lucide/vue';
import Alert from '@/components/v2/Alert.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Combobox, { ComboboxOption } from '@/components/v2/Combobox.vue';
import {
  DashboardSubscription,
  DashboardSubscriptionRequest,
  SubscribableDashboard,
  SubscriptionCadence,
  SubscriptionFormat,
} from '@/types/DashboardSubscription';
import { arrivalPreview, browserTimeZone, dayOfMonthOptions, dayOfWeekOptions, hourOptions, timeZoneOptions } from '@/utilities/SubscriptionSchedule';

const props = defineProps<{
  dashboards: SubscribableDashboard[];
  subscription?: DashboardSubscription | null;
  initialDashboardId?: number;
  save: (request: DashboardSubscriptionRequest) => Promise<void>;
}>();

const emit = defineEmits<{
  cancel: [];
}>();

const isEditing = computed(() => !!props.subscription);

const dashboardId = ref(String(props.subscription?.accountTableauId ?? props.initialDashboardId ?? ''));
const cadence = ref<SubscriptionCadence>(props.subscription?.cadence ?? 'WEEKLY');
const dayOfWeek = ref(props.subscription?.dayOfWeek ?? 1);
const dayOfMonth = ref(props.subscription?.dayOfMonth ?? 1);
const sendAtLocalTime = ref(props.subscription?.sendAtLocalTime ?? '08:00');
const timeZone = ref(props.subscription?.timeZone ?? browserTimeZone());
const outputFormat = ref<SubscriptionFormat>(props.subscription?.outputFormat ?? 'LINK');
const saving = ref(false);
const saveError = ref<string>();

const dashboardLabel = (dashboard: SubscribableDashboard) => {
  if (dashboard.accountName) {
    return `${dashboard.name} — ${dashboard.accountName}`;
  }
  return dashboard.isAdvisory ? `${dashboard.name} — Advisory` : dashboard.name;
};

// One subscription per dashboard, so a new one only offers dashboards not yet subscribed to.
const dashboardOptions = computed<ComboboxOption[]>(() =>
  props.dashboards
    .filter((dashboard) => isEditing.value || !dashboard.subscriptionId)
    .map((dashboard) => ({ value: String(dashboard.accountTableauId), label: dashboardLabel(dashboard) })),
);

const selectedDashboard = computed(() => props.dashboards.find((dashboard) => String(dashboard.accountTableauId) === dashboardId.value));

const pdfAvailable = computed(() => (props.subscription ? props.subscription.pdfAvailable : !!selectedDashboard.value?.pdfAvailable));

// A dashboard can lose its place on the render allow-list after someone subscribed for a PDF.
const pdfWithdrawn = computed(() => props.subscription?.outputFormat === 'PDF' && !props.subscription.pdfAvailable);

watch(
  pdfAvailable,
  (available) => {
    if (!available) {
      outputFormat.value = 'LINK';
    }
  },
  { immediate: true },
);

const formatHint = computed(() => {
  if (pdfWithdrawn.value) {
    return { tone: 'text-amber-700', text: 'PDF is no longer available for this dashboard, so it will be sent as a link.' };
  }
  if (!dashboardId.value) {
    return { tone: 'text-gray-500', text: 'Select a dashboard to see available formats.' };
  }
  if (pdfAvailable.value) {
    return { tone: 'text-gray-500', text: 'PDF is available for this dashboard.' };
  }
  return { tone: 'text-amber-700', text: 'This dashboard is delivered as a link only.' };
});

const preview = computed(() =>
  arrivalPreview({
    cadence: cadence.value,
    sendAtLocalTime: sendAtLocalTime.value,
    dayOfWeek: dayOfWeek.value,
    dayOfMonth: dayOfMonth.value,
    timeZone: timeZone.value,
  }),
);

const canSave = computed(() => !!dashboardId.value && !saving.value);

async function submit() {
  if (!canSave.value) {
    return;
  }
  saving.value = true;
  saveError.value = undefined;
  try {
    await props.save({
      accountTableauId: isEditing.value ? undefined : Number(dashboardId.value),
      cadence: cadence.value,
      sendAtLocalTime: sendAtLocalTime.value,
      dayOfWeek: cadence.value === 'WEEKLY' ? dayOfWeek.value : null,
      dayOfMonth: cadence.value === 'MONTHLY' ? dayOfMonth.value : null,
      timeZone: timeZone.value,
      outputFormat: outputFormat.value,
    });
  } catch (error) {
    saveError.value = error instanceof Error ? error.message : 'The subscription could not be saved.';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <form
    class="rounded-lg border border-solid border-gray-200 bg-gray-50 p-4"
    data-testid="dashboard-subscription-form"
    @submit.prevent="submit">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <div class="sm:col-span-3">
        <label class="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase">Dashboard</label>
        <Combobox
          v-model="dashboardId"
          :options="dashboardOptions"
          :disabled="isEditing || !!initialDashboardId"
          placeholder="Select a dashboard…"
          data-testid="subscription-dashboard" />
        <p class="mt-1 text-xs text-gray-500">Only dashboards you can open are listed.</p>
      </div>

      <div>
        <label
          for="subscription-cadence"
          class="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase"
          >Cadence</label
        >
        <select
          id="subscription-cadence"
          v-model="cadence"
          class="select-bordered select w-full"
          data-testid="subscription-cadence">
          <option value="DAILY">Daily</option>
          <option value="WEEKLY">Weekly</option>
          <option value="MONTHLY">Monthly</option>
        </select>
      </div>

      <div :class="{ invisible: cadence === 'DAILY' }">
        <template v-if="cadence === 'MONTHLY'">
          <label
            for="subscription-day-of-month"
            class="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase"
            >Day of month</label
          >
          <select
            id="subscription-day-of-month"
            v-model.number="dayOfMonth"
            class="select-bordered select w-full"
            data-testid="subscription-day-of-month">
            <option
              v-for="option in dayOfMonthOptions(subscription?.dayOfMonth)"
              :key="option.value"
              :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </template>
        <template v-else>
          <label
            for="subscription-day-of-week"
            class="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase"
            >Day of week</label
          >
          <select
            id="subscription-day-of-week"
            v-model.number="dayOfWeek"
            class="select-bordered select w-full"
            data-testid="subscription-day-of-week">
            <option
              v-for="option in dayOfWeekOptions()"
              :key="option.value"
              :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </template>
      </div>

      <div>
        <label
          for="subscription-send-time"
          class="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase"
          >Send time</label
        >
        <select
          id="subscription-send-time"
          v-model="sendAtLocalTime"
          class="select-bordered select w-full"
          data-testid="subscription-send-time">
          <option
            v-for="option in hourOptions()"
            :key="option.value"
            :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="sm:col-span-2">
        <label
          for="subscription-time-zone"
          class="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase"
          >Timezone</label
        >
        <select
          id="subscription-time-zone"
          v-model="timeZone"
          class="select-bordered select w-full"
          data-testid="subscription-time-zone">
          <option
            v-for="option in timeZoneOptions(subscription?.timeZone)"
            :key="option.value"
            :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="sm:col-span-3">
        <span class="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase">Format</span>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-solid px-3 py-2 text-sm"
            :class="outputFormat === 'LINK' ? 'border-sky-400 bg-sky-50 text-sky-900' : 'border-gray-300 bg-white'"
            data-testid="subscription-format-link"
            @click="outputFormat = 'LINK'">
            <LinkIcon :size="16" />
            Link to dashboard
          </button>
          <button
            type="button"
            class="flex flex-1 items-center gap-2 rounded-lg border border-solid px-3 py-2 text-sm"
            :class="[
              outputFormat === 'PDF' ? 'border-sky-400 bg-sky-50 text-sky-900' : 'border-gray-300 bg-white',
              pdfAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
            ]"
            :disabled="!pdfAvailable"
            data-testid="subscription-format-pdf"
            @click="outputFormat = 'PDF'">
            <FileTextIcon :size="16" />
            PDF + link
          </button>
        </div>
        <p
          class="mt-1 text-xs"
          :class="formatHint.tone"
          data-testid="subscription-format-hint">
          {{ formatHint.text }}
        </p>
      </div>

      <p
        class="flex items-center gap-1.5 text-sm text-sky-900 sm:col-span-3"
        data-testid="subscription-arrival">
        <ClockIcon :size="14" />
        {{ preview }}
      </p>
    </div>

    <Alert
      v-if="saveError"
      type="error"
      class="mt-3">
      {{ saveError }}
    </Alert>

    <div class="mt-4 flex justify-end gap-2 border-t border-solid border-gray-200 pt-3">
      <AppButton
        type="button"
        @click="emit('cancel')">
        Cancel
      </AppButton>
      <AppButton
        primary
        type="submit"
        :loading="saving"
        :disabled="!canSave"
        data-testid="subscription-save">
        {{ isEditing ? 'Save changes' : 'Create subscription' }}
      </AppButton>
    </div>
  </form>
</template>
